import React, { useEffect, useState, useRef, createContext, useContext } from 'react';
import axios from 'axios';
import configData from '../config.json';

export const StampWalletContext = createContext();

// Standardized wallet token key (§9.5). This is a SEPARATE key from `guest-uui`
// (the browse-gate timestamp) — never reuse one for the other. The signed wallet
// JWT identifies the wallet owner and travels ONLY in the Authorization header (A4),
// never as a query param. Registered users fall back to their `user_profile` token.
const WALLET_TOKEN_KEY = 'hw-wallet-token';

const readWalletToken = () => {
  // A registered user's profile token doubles as the wallet identity, and it
  // WINS over a lingering guest token: the merge-on-signup flow (§6.5) clears
  // `hw-wallet-token`, so one surviving alongside a login is either already
  // merged or was minted by a logged-in scan — in both cases the account is
  // the authoritative (and richer) wallet, and a stale guest token must not
  // shadow it with an empty one.
  //
  // The two reads are guarded SEPARATELY on purpose. A corrupt `user_profile`
  // is not a reason to forget a guest's wallet: one try around both would throw
  // past the fallback and silently orphan the stamps of anyone who scanned a
  // venue QR on a browser with a half-written profile.
  try {
    const profile = localStorage.getItem('user_profile');
    if (profile) {
      const parsed = JSON.parse(profile);
      if (parsed?.token) return parsed.token;
    }
  } catch (e) {
    // Malformed profile — fall through to the guest wallet token.
  }

  try {
    const walletToken = localStorage.getItem(WALLET_TOKEN_KEY);
    if (walletToken) return walletToken;
  } catch (e) {
    // Storage unavailable (private mode / blocked) — fail-safe to anonymous.
  }

  return '';
};

export function StampWalletWrapper({ children }) {
  // Identity: the signed wallet token, resolved once at mount from localStorage.
  const [walletOwnerToken, setWalletOwnerToken] = useState('');
  // Per-product progress, keyed by productId (server perStampIdHash=false). §9.2
  const [progressByProduct, setProgressByProduct] = useState({});
  // Full wallet cards + history (the /stamp/wallet/mobile/fetch contract).
  const [walletStamps, setWalletStamps] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  // The voucher the customer chose to redeem in-cart (display only — the server is
  // authoritative on price/expiry/eligibility and constructs the reward line). §9.3
  const [stagedVoucher, setStagedVoucher] = useState(null);
  // The staged voucher's display discount (the freed cart item's value). Lifted
  // here so BOTH the cart summary and the sticky footer total can subtract it.
  const [voucherDiscount, setVoucherDiscount] = useState(0);
  // True while the /wallet page's fetchWallet is in flight (drives its spinner).
  const [loading, setLoading] = useState(false);

  // Latest token kept in a ref so the actions read it without being recreated.
  const tokenRef = useRef('');
  tokenRef.current = walletOwnerToken;

  useEffect(() => {
    setWalletOwnerToken(readWalletToken());
  }, []);

  const authHeaders = () => {
    const token = tokenRef.current || readWalletToken();
    // Self-heal the exposed identity. This provider sits ABOVE BrowserRouter, so
    // its mount effect runs once per page load — a token minted later in the same
    // session (a QR scan landing on /q/:token, or a login that does not reload)
    // would otherwise never reach `walletOwnerToken`, and consumers that read it
    // directly (VoucherRedeemBlock) would call the API unauthenticated.
    if (token && token !== tokenRef.current) {
      tokenRef.current = token;
      setWalletOwnerToken(token);
    }
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Per-product progress for a restaurant's menu (§9.2). Optional-auth endpoint:
  // anonymous users still get every qualifying product at count 0 (so the menu marks
  // stampcard products); signed-in users get live progress on top. No active campaign
  // → empty object → no badges. Any error clears progress so we never show stale dots.
  const fetchMenuProgress = async (restaurantId) => {
    if (!restaurantId) {
      setProgressByProduct({});
      return {};
    }
    try {
      const response = await axios.get(
        `${configData.SERVER_URL}/stamp/web/menu/progress?restaurant=${restaurantId}`,
        { headers: authHeaders() },
      );
      const data = response?.data && typeof response.data === 'object' ? response.data : {};
      setProgressByProduct(data);
      return data;
    } catch (e) {
      // Fail-safe: clear to nothing on any error (anonymous, no campaign, network).
      console.log('Stamp menu progress:', e.message);
      setProgressByProduct({});
      return {};
    }
  };

  // Full wallet (cards + vouchers + history) for the /wallet page. §9.4
  // Optional-auth: anonymous → empty wallet. Fail-safe-to-nothing on any error.
  const fetchWallet = async (restaurantId) => {
    setLoading(true);
    try {
      const token = tokenRef.current || readWalletToken();
      // No identity → empty wallet, mirror the backend's anonymous response.
      if (!token) {
        setWalletStamps([]);
        setVouchers([]);
        return { cards: [], vouchers: [], history: [] };
      }

      const query = restaurantId ? `?restaurant=${restaurantId}` : '';
      // /stamp/web/wallet returns { cards, vouchers } — the mobile/fetch endpoint
      // only returns cards (a bare array), so vouchers never reached the Ready tab.
      const response = await axios.get(
        `${configData.SERVER_URL}/stamp/web/wallet${query}`,
        { headers: authHeaders() },
      );

      const data = response?.data || {};
      const cards = Array.isArray(data?.cards) ? data.cards : Array.isArray(data) ? data : [];
      // /stamp/web/wallet returns vouchers in camelCase (status/code/expiresAt);
      // the Wallet UI reads PascalCase (Status/Code/ExpiresAt). Normalize here so
      // there's one canonical voucher shape regardless of the endpoint's casing.
      const walletVouchers = (Array.isArray(data?.vouchers) ? data.vouchers : []).map((v) => ({
        ...v,
        _id: v._id,
        Status: v.Status || v.status,
        Code: v.Code || v.code,
        ExpiresAt: v.ExpiresAt || v.expiresAt || null,
        Campaign: v.Campaign || v.campaign,
        Restaurant: v.Restaurant || v.restaurant,
        // Frozen multi-location allow-list (OWNER_ONLY -> [owner], ANY -> owner
        // + affiliated). Empty/absent means unrestricted. Without this alias the
        // chain vouchers never match a sister restaurant in the cart.
        RedeemableAt: v.RedeemableAt || v.redeemableAt || [],
        RewardSnapshot: v.RewardSnapshot || v.rewardSnapshot,
        EarnedAt: v.EarnedAt || v.earnedAt,
        RedeemedAt: v.RedeemedAt || v.redeemedAt || null,
      }));

      setWalletStamps(cards);
      setVouchers(walletVouchers);
      return { cards, vouchers: walletVouchers, history: data?.history || [] };
    } catch (e) {
      console.log('Stamp wallet fetch:', e.message);
      setWalletStamps([]);
      setVouchers([]);
      return { cards: [], vouchers: [], history: [] };
    } finally {
      setLoading(false);
    }
  };

  // Stage a chosen voucher + reward for in-cart redemption (§9.3). The client only
  // ever carries {voucherCode, rewardProductId} into the order payload — never
  // isReward/price; the server constructs the reward line.
  const stageVoucher = (voucher, rewardProductId) => {
    if (!voucher) {
      setStagedVoucher(null);
      return;
    }
    setStagedVoucher({ voucher, rewardProductId: rewardProductId || null });
  };

  const clearStagedVoucher = () => { setStagedVoucher(null); setVoucherDiscount(0); };

  // Re-pull the server's view of the wallet (cards + vouchers) and the menu's
  // per-product progress. Deliberately does NOT touch the staged voucher: this
  // also runs when an OLD order completes while the customer is building a new
  // cart, and unstaging their chosen voucher there would silently change the
  // total in front of them.
  const refreshStamps = async (restaurantId) => {
    await Promise.all([fetchWallet(restaurantId), fetchMenuProgress(restaurantId)]);
  };

  // After an order is submitted, the reservation/burn changes wallet state — clear
  // the staged voucher and re-pull wallet + progress so the UI reflects the server.
  const refreshAfterOrder = async (restaurantId) => {
    setStagedVoucher(null);
    await refreshStamps(restaurantId);
  };

  return (
    <StampWalletContext.Provider
      value={{
        walletOwnerToken,
        setWalletOwnerToken,
        progressByProduct,
        walletStamps,
        vouchers,
        stagedVoucher,
        voucherDiscount,
        setVoucherDiscount,
        loading,
        fetchMenuProgress,
        fetchWallet,
        stageVoucher,
        clearStagedVoucher,
        refreshStamps,
        refreshAfterOrder,
      }}
    >
      {children}
    </StampWalletContext.Provider>
  );
}

export function useStampWallet() {
  return useContext(StampWalletContext);
}
